import React, { ReactNode, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Stack } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { ProposalsMetricsDashboard } from '../dashboard/ProposalsMetricsDashboard';
import { ProposalsViewTypes } from '../../types';
import { HubTable } from '../tables/hub/HubTable';
import { hubTableApiQueryByType } from '../../constants';
import { HubProposal, HubProposalPaginatedResponse } from '../../api/types';
import { ProposalsTypeManager } from '../components/ProposalsTypeManager';
import { ProposalsFilters } from '../components/ProposalsFilters';
import { useAccessControlProposalHubTable } from '../../hooks/useAccessControlProposalHubTable';

const DEFAULT_PAGE_SIZE = 25;

export const ProposalsPage = () => {
  const history = useHistory();

  const { hasAccessToAllProposals } = useAccessControlProposalHubTable();

  const dynamicsAccountId = useSelector((state: any) => state.account.dynamicsAccountId);

  const [proposalsType, setProposalsType] = useState<ProposalsViewTypes>(ProposalsViewTypes.My);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // Sorting state
  const [sortKey, setSortKey] = useState<string>('createdDateTime');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Filters state
  const [filters, setFilters] = useState<Record<string, any>>({});

  // Reset pagination, sorting, and filters when switching between tabs
  useEffect(() => {
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setSortKey('createdDateTime');
    setSortOrder('DESC');
    setFilters({});
  }, [proposalsType]);

  const { data: proposalsHubData, loading: isProposalsLoading } = useQuery<HubProposalPaginatedResponse>(
    hubTableApiQueryByType[proposalsType],
    {
      variables: {
        input: {
          pagination: {
            page: currentPage,
            perPage: pageSize,
          },
          sort: {
            key: sortKey,
            value: sortOrder,
          },
          filters: {
            ...filters,
          },
        },
      },
      nextFetchPolicy: 'cache-first',
      fetchPolicy: 'cache-and-network',
      skip: proposalsType === ProposalsViewTypes.Dashboard,
    }
  );

  const handleGoToProposal = (proposal: HubProposal) => {
    window.open(`/proposalDetail/${proposal.id}?from=/proposals-hub`, '_blank');
  };

  const handleGoToProposalDetail = (proposal: HubProposal) => {
    if (!proposal.id || !dynamicsAccountId) return;

    const link = generateProposalPageURL(proposal.id, dynamicsAccountId);
    window.open(link, '_blank');
  };

  const handleEditProposal = (proposal: HubProposal) => {
    history.push({
      hash: '#desking',
      pathname: `/vo/${proposal.vendorOpportunityId}`,
      state: {
        proposalId: proposal.id,
        proposalSaleType: proposal.saleType,
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleSortChange = (key: string, order: 'ASC' | 'DESC') => {
    setSortKey(key);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const handleFilterChange = (filterKey: string, filterValue: any) => {
    setFilters(prev => {
      if (filterValue === null || filterValue === undefined || filterValue === '') {
        const { [filterKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [filterKey]: filterValue };
    });
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const renderTabContentByType: Record<ProposalsViewTypes, ReactNode> = {
    [ProposalsViewTypes.My]: (
      <>
        <ProposalsFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          disabled={isProposalsLoading}
        />

        <HubTable
          loading={isProposalsLoading}
          onEditProposal={handleEditProposal}
          onGoToProposal={handleGoToProposal}
          tableData={proposalsHubData?.proposals?.data ?? []}
          onGoToProposalDetail={handleGoToProposalDetail}
          pagination={{
            page: proposalsHubData?.proposals?.page ?? 1,
            perPage: proposalsHubData?.proposals?.perPage ?? pageSize,
            total: proposalsHubData?.proposals?.total ?? 0,
            totalPages: proposalsHubData?.proposals?.totalPages ?? 0,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
          sorting={{
            sortKey,
            sortOrder,
            onSortChange: handleSortChange,
          }}
          filtering={{
            filters,
            onFilterChange: handleFilterChange,
          }}
        />
      </>
    ),
    [ProposalsViewTypes.All]: (
      <>
        <ProposalsFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          disabled={isProposalsLoading}
        />

        <HubTable
          loading={isProposalsLoading}
          onEditProposal={handleEditProposal}
          onGoToProposal={handleGoToProposal}
          tableData={proposalsHubData?.proposals?.data ?? []}
          onGoToProposalDetail={handleGoToProposalDetail}
          pagination={{
            page: proposalsHubData?.proposals?.page ?? 1,
            perPage: proposalsHubData?.proposals?.perPage ?? pageSize,
            total: proposalsHubData?.proposals?.total ?? 0,
            totalPages: proposalsHubData?.proposals?.totalPages ?? 0,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
          sorting={{
            sortKey,
            sortOrder,
            onSortChange: handleSortChange,
          }}
          filtering={{
            filters,
            onFilterChange: handleFilterChange,
          }}
        />
      </>
    ),
    [ProposalsViewTypes.Dashboard]: <ProposalsMetricsDashboard />,
  };

  return (
    <PageContainer>
      <CardContainer>
        <Stack gap="1rem" height="100%">
          <RenderWithCondition condition={hasAccessToAllProposals}>
            <ProposalsTypeManager currentType={proposalsType} onTypeChange={setProposalsType} />
          </RenderWithCondition>

          {renderTabContentByType[proposalsType]}
        </Stack>
      </CardContainer>
    </PageContainer>
  );
};
